import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import StockCandlestickPanel from "@/components/charts/StockCandlestickPanel";
import stockMarketService from "@/services/stockMarketService";
import type { BreakoutStatus, StockHistoryResponse, StockScannerRow } from "@/types";
import { extractErrorMessage } from "@/utils/errors";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip, Legend);

// Stock Scoring Standardization (2026-09-13): this page now reads the same
// canonical Breakout Score Engine V2 data as Market Opportunities/Market Cap
// Segments/Portfolio Tracker, replacing the retired Market Scanner's 5-band
// bullish/bearish `ScannerSignal` palette — see
// `BREAKOUT_STATUS_COLORS`/`BREAKOUT_STATUS_LABELS` on `MarketOpportunitiesPage`.
const BREAKOUT_STATUS_COLORS: Record<BreakoutStatus, string> = {
  STRONG_BREAKOUT: "#1B5E20",
  BREAKOUT_CANDIDATE: "#2E7D32",
  DEVELOPING_BREAKOUT: "#F9A825",
  NO_CONFIRMED_BREAKOUT: "#9E9E9E",
  INSUFFICIENT_DATA: "#5C6BC0",
  FAILED_BREAKOUT: "#C62828",
};

const BREAKOUT_STATUS_LABELS: Record<BreakoutStatus, string> = {
  STRONG_BREAKOUT: "Strong Breakout",
  BREAKOUT_CANDIDATE: "Breakout Candidate",
  DEVELOPING_BREAKOUT: "Developing Breakout",
  NO_CONFIRMED_BREAKOUT: "No Confirmed Breakout",
  INSUFFICIENT_DATA: "Insufficient Data",
  FAILED_BREAKOUT: "Failed Breakout",
};

function num(value: string | null | undefined, decimals = 2): string {
  if (value === null || value === undefined) return "—";
  const n = Number.parseFloat(value);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function StatusChip({ status }: { status?: BreakoutStatus | null }) {
  if (!status) return <Chip label="Not scored yet" size="small" variant="outlined" />;
  return (
    <Chip
      label={BREAKOUT_STATUS_LABELS[status]}
      size="small"
      sx={{ bgcolor: BREAKOUT_STATUS_COLORS[status], color: "#fff", fontWeight: 600 }}
    />
  );
}

/** Canonical Breakout Score (0-100) cell, color-coded by `status` —
 * replaces the retired Market Scanner's combined bullish/bearish net score
 * (-100..100). `null` means this stock hasn't been through the Breakout
 * Score Engine yet (accepted, documented gap — see `StockScannerRow.breakout_score`). */
function BreakoutScoreCell({ value, status }: { value?: string | null; status?: BreakoutStatus | null }) {
  if (value === null || value === undefined) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  const n = Number.parseFloat(value);
  return (
    <Typography
      variant="body2"
      fontWeight={700}
      sx={{ color: status ? BREAKOUT_STATUS_COLORS[status] : "text.secondary" }}
    >
      {Number.isNaN(n) ? "—" : Math.round(n)}
    </Typography>
  );
}

/** Detail panel: 6-month price+EMA chart, RSI chart, MACD chart, Breakout Score history, status history, OHLC table. */
function StockDetailDialog({ symbol, onClose }: { symbol: string; onClose: () => void }) {
  const [data, setData] = useState<StockHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await stockMarketService.getStockHistory(symbol, 6);
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) setError(extractErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [symbol]);

  const priceChart = useMemo(() => {
    if (!data) return null;
    const labels = data.prices.map((p) => p.trade_date);
    const emaByDate = new Map(data.indicators.map((i) => [i.trade_date, i]));
    return {
      labels,
      datasets: [
        {
          label: "Close",
          data: data.prices.map((p) => Number(p.close)),
          borderColor: "#0F3D63",
          backgroundColor: "#0F3D63",
          pointRadius: 0,
          tension: 0.1,
        },
        {
          label: "EMA 20",
          data: labels.map((d) => {
            const v = emaByDate.get(d)?.ema_20;
            return v ? Number(v) : null;
          }),
          borderColor: "#00B386",
          pointRadius: 0,
          tension: 0.1,
        },
        {
          label: "EMA 50",
          data: labels.map((d) => {
            const v = emaByDate.get(d)?.ema_50;
            return v ? Number(v) : null;
          }),
          borderColor: "#FB8C00",
          pointRadius: 0,
          tension: 0.1,
        },
        {
          label: "EMA 200",
          data: labels.map((d) => {
            const v = emaByDate.get(d)?.ema_200;
            return v ? Number(v) : null;
          }),
          borderColor: "#8E24AA",
          pointRadius: 0,
          tension: 0.1,
        },
      ],
    };
  }, [data]);

  const rsiChart = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.indicators.map((i) => i.trade_date),
      datasets: [
        {
          label: "RSI (14)",
          data: data.indicators.map((i) => (i.rsi_14 ? Number(i.rsi_14) : null)),
          borderColor: "#1976D2",
          pointRadius: 0,
          tension: 0.1,
        },
      ],
    };
  }, [data]);

  const macdChart = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.indicators.map((i) => i.trade_date),
      datasets: [
        {
          label: "MACD",
          data: data.indicators.map((i) => (i.macd ? Number(i.macd) : null)),
          borderColor: "#0F3D63",
          pointRadius: 0,
          tension: 0.1,
        },
        {
          label: "Signal",
          data: data.indicators.map((i) => (i.macd_signal ? Number(i.macd_signal) : null)),
          borderColor: "#C62828",
          pointRadius: 0,
          tension: 0.1,
        },
      ],
    };
  }, [data]);

  const scoreChart = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.scanner_history.map((s) => s.trade_date),
      datasets: [
        {
          label: "Breakout Score",
          data: data.scanner_history.map((s) => Number(s.breakout_score)),
          borderColor: "#2E7D32",
          pointRadius: 0,
          tension: 0.1,
        },
      ],
    };
  }, [data]);

  const chartOptions = { responsive: true, plugins: { legend: { position: "top" as const } }, scales: { x: { display: false } } };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{data ? `${data.symbol} — ${data.company_name}` : symbol}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : data ? (
          <Stack spacing={3} mt={1}>
            {/* Phase 1 TradingView-style (Lightweight Charts) candlestick + volume. Shares the 6-month
                history this dialog already loaded; other ranges fetch the same endpoint. */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <StockCandlestickPanel symbol={symbol} initialHistory={data} initialMonths={6} />
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Price (6mo) with EMA 20 / 50 / 200
              </Typography>
              {priceChart && <Line data={priceChart} options={chartOptions} />}
            </Paper>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    RSI (14)
                  </Typography>
                  {rsiChart && <Line data={rsiChart} options={chartOptions} />}
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    MACD / Signal
                  </Typography>
                  {macdChart && <Line data={macdChart} options={chartOptions} />}
                </Paper>
              </Grid>
            </Grid>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Historical Breakout Score
              </Typography>
              {scoreChart && data.scanner_history.length > 0 ? (
                <Line data={scoreChart} options={chartOptions} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Not enough warm-up history yet for a Breakout Score.
                </Typography>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Status History (most recent 15)
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
                {data.scanner_history
                  .slice(-15)
                  .reverse()
                  .map((s) => (
                    <Tooltip key={s.trade_date} title={`${s.trade_date}: Breakout Score ${s.breakout_score}`}>
                      <Chip
                        label={`${s.trade_date.slice(5)} · ${BREAKOUT_STATUS_LABELS[s.status]}`}
                        size="small"
                        sx={{ bgcolor: BREAKOUT_STATUS_COLORS[s.status], color: "#fff" }}
                      />
                    </Tooltip>
                  ))}
                {data.scanner_history.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No status history yet.
                  </Typography>
                )}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, maxHeight: 320, overflow: "auto" }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                OHLC Data (most recent first)
              </Typography>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Open</TableCell>
                    <TableCell align="right">High</TableCell>
                    <TableCell align="right">Low</TableCell>
                    <TableCell align="right">Close</TableCell>
                    <TableCell align="right">Volume</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...data.prices]
                    .reverse()
                    .map((p) => (
                      <TableRow key={p.trade_date}>
                        <TableCell>{p.trade_date}</TableCell>
                        <TableCell align="right">{num(p.open)}</TableCell>
                        <TableCell align="right">{num(p.high)}</TableCell>
                        <TableCell align="right">{num(p.low)}</TableCell>
                        <TableCell align="right">{num(p.close)}</TableCell>
                        <TableCell align="right">{p.volume.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Paper>
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

const TABLE_COLUMNS: { field: string; label: string; align: "left" | "right" }[] = [
  { field: "symbol", label: "Symbol", align: "left" },
  { field: "company_name", label: "Company", align: "left" },
  { field: "status", label: "Status", align: "right" },
  { field: "breakout_score", label: "Breakout Score", align: "right" },
  { field: "close_price", label: "Close", align: "right" },
  { field: "change_percent", label: "Change %", align: "right" },
  { field: "ema_20", label: "EMA20", align: "right" },
  { field: "ema_50", label: "EMA50", align: "right" },
  { field: "ema_200", label: "EMA200", align: "right" },
  { field: "rsi_14", label: "RSI", align: "right" },
  { field: "macd", label: "MACD", align: "right" },
  { field: "volume", label: "Volume", align: "right" },
  { field: "avg_volume_20", label: "Avg Volume", align: "right" },
  { field: "sector", label: "Sector", align: "left" },
  { field: "last_updated", label: "Last Updated", align: "right" },
];

export function StockDataPage() {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  // Default: sorted highest Breakout Score first, so the most promising
  // stocks are at the top without the user having to do anything.
  const [sortBy, setSortBy] = useState("breakout_score");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [rows, setRows] = useState<StockScannerRow[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({
    total_stocks: 0,
    strong_breakout: 0,
    breakout_candidate: 0,
    developing_breakout: 0,
    no_confirmed_breakout: 0,
    not_yet_scored: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [detailSymbol, setDetailSymbol] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await stockMarketService.listScanner({
        page: page + 1,
        page_size: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      setRows(response.items);
      setTotal(response.meta.total);
      setSummary(response.summary);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortBy, sortOrder]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" alignItems="flex-start" mb={2} flexWrap="wrap" gap={2}>
        <Tooltip title="Refresh">
          <IconButton onClick={load} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ---- Summary metrics ---- */}
      <Stack direction="row" flexWrap="wrap" alignItems="baseline" columnGap={1.5} rowGap={0.5} mb={2}>
        {[
          { label: "Total Stocks", value: summary.total_stocks, color: "text.primary" },
          { label: "Strong Breakout", value: summary.strong_breakout, color: BREAKOUT_STATUS_COLORS.STRONG_BREAKOUT },
          { label: "Breakout Candidate", value: summary.breakout_candidate, color: BREAKOUT_STATUS_COLORS.BREAKOUT_CANDIDATE },
          { label: "Developing Breakout", value: summary.developing_breakout, color: BREAKOUT_STATUS_COLORS.DEVELOPING_BREAKOUT },
          { label: "No Confirmed Breakout", value: summary.no_confirmed_breakout, color: BREAKOUT_STATUS_COLORS.NO_CONFIRMED_BREAKOUT },
        ].map((card, index) => (
          <Box key={card.label} component="span" sx={{ display: "inline-flex", alignItems: "baseline", gap: 1.5 }}>
            {index > 0 && (
              <Typography component="span" variant="body2" color="text.disabled" sx={{ userSelect: "none" }}>
                |
              </Typography>
            )}
            <Typography component="span" variant="body2" sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>
              {card.label}: <Box component="span" sx={{ color: card.color, fontWeight: 700 }}>{card.value}</Box>
            </Typography>
          </Box>
        ))}
      </Stack>

      {/* ---- Scanner table ---- */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {TABLE_COLUMNS.map((col) => (
                  <TableCell
                    key={col.field}
                    align={col.align}
                    onClick={() => handleSort(col.field)}
                    sx={{ cursor: "pointer", whiteSpace: "nowrap" }}
                  >
                    {col.label}
                    {sortBy === col.field ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.symbol} hover sx={{ cursor: "pointer" }} onClick={() => setDetailSymbol(row.symbol)}>
                  <TableCell sx={{ fontWeight: 600 }}>{row.symbol}</TableCell>
                  <TableCell>{row.company_name}</TableCell>
                  <TableCell align="right">
                    <StatusChip status={row.status} />
                  </TableCell>
                  <TableCell align="right">
                    <BreakoutScoreCell value={row.breakout_score} status={row.status} />
                  </TableCell>
                  <TableCell align="right">{num(row.close_price)}</TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: row.change_percent
                        ? Number(row.change_percent) >= 0
                          ? "success.main"
                          : "error.main"
                        : "text.secondary",
                    }}
                  >
                    {row.change_percent ? `${Number(row.change_percent) >= 0 ? "+" : ""}${num(row.change_percent)}%` : "—"}
                  </TableCell>
                  <TableCell align="right">{num(row.ema_20)}</TableCell>
                  <TableCell align="right">{num(row.ema_50)}</TableCell>
                  <TableCell align="right">{num(row.ema_200)}</TableCell>
                  <TableCell align="right">{num(row.rsi_14)}</TableCell>
                  <TableCell align="right">{num(row.macd)}</TableCell>
                  <TableCell align="right">{row.volume.toLocaleString()}</TableCell>
                  <TableCell align="right">{row.avg_volume_20 ? Math.round(Number(row.avg_volume_20)).toLocaleString() : "—"}</TableCell>
                  <TableCell>{row.sector ?? "—"}</TableCell>
                  <TableCell align="right">{new Date(row.last_updated).toLocaleString("en-IN")}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={TABLE_COLUMNS.length} align="center">
                    No stock data yet — an admin needs to import history first.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      )}
      {totalPages > 0 && (
        <Typography variant="caption" color="text.secondary">
          Page {page + 1} of {totalPages}
        </Typography>
      )}

      {detailSymbol && <StockDetailDialog symbol={detailSymbol} onClose={() => setDetailSymbol(null)} />}

    </Box>
  );
}

export default StockDataPage;
